<template>
  <div class="titlebar">
    <div class="titlebar-row">
      <div class="titlebar-main">
        <template v-if="renaming">
          <input
            ref="input"
            v-model="value"
            class="title-input"
            type="text"
            @keyup.enter="commit"
            @keyup.esc="cancel"
            @blur="commit"
          />
        </template>
        <template v-else>
          <span class="title-name">{{ name }}</span>
          <StIconButton
            icon="pencil"
            :label="`Rename ${noun}`"
            size="sm"
            on-shell
            @click="startRename"
          />
        </template>
        <span v-if="subtitle" class="title-subtitle">{{ subtitle }}</span>
      </div>
      <StIconButton
        v-if="!hideDuplicate"
        icon="copy"
        :label="`Duplicate ${noun}`"
        on-shell
        @click="$emit('duplicate')"
      />
      <StIconButton
        v-if="!hideDelete"
        icon="trash"
        :label="`Delete ${noun}`"
        variant="danger"
        on-shell
        @click="$emit('delete')"
      />
    </div>
    <!-- Editable id, shown under the name (and only when Settings → Show ids is on,
         which StIdField gates internally). -->
    <StIdField
      v-if="entityId !== undefined"
      class="titlebar-id"
      on-shell
      :model-value="entityId"
      :prefix="idPrefix"
      @update:model-value="$emit('update:entityId', $event)"
    />
    <div v-if="$slots.tabs" class="titlebar-tabs">
      <slot name="tabs" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, nextTick } from 'vue';
import { StIconButton, StIdField } from '../ui/index.js';

const props = withDefaults(
  defineProps<{
    name: string;
    subtitle?: string;
    /** Noun used in the rename/duplicate/delete tooltips, e.g. "Battleplan", "Document". */
    noun?: string;
    /** Hide the duplicate / delete actions (e.g. fixed entries that can't be removed). */
    hideDuplicate?: boolean;
    hideDelete?: boolean;
    /** Entity id to expose under the name. Omit for entities without an id. */
    entityId?: string;
    /** Prefix used when regenerating the id (e.g. "unit" → "unit-<uuid>"). */
    idPrefix?: string;
  }>(),
  { subtitle: '', noun: 'Battleplan' },
);
const emit = defineEmits<{
  rename: [next: string];
  delete: [];
  duplicate: [];
  'update:entityId': [next: string];
}>();

const renaming = ref(false);
const value = ref('');
const input = ref<HTMLInputElement | null>(null);

async function startRename() {
  value.value = props.name;
  renaming.value = true;
  await nextTick();
  input.value?.focus();
  input.value?.select();
}

function cancel() {
  renaming.value = false;
}

function commit() {
  if (!renaming.value) return; // already committed/cancelled
  const next = value.value.trim();
  renaming.value = false;
  if (!next || next === props.name) return;
  emit('rename', next);
}
</script>

<style scoped>
.titlebar {
  display: flex;
  flex-direction: column;
  gap: var(--st-space-2);
  padding: var(--st-space-2) var(--st-space-3);
  background: var(--st-shell-bg);
  color: var(--st-shell-text);
}

.titlebar-row {
  display: flex;
  align-items: center;
  gap: var(--st-space-3);
}

.titlebar-main {
  display: flex;
  align-items: baseline;
  gap: var(--st-space-2);
  flex: 1;
  min-width: 0;
}

.titlebar-tabs {
  display: flex;
}

.title-name {
  font-size: var(--st-text-lg);
  font-weight: var(--st-weight-bold);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.title-subtitle {
  font-size: var(--st-text-xs);
  font-weight: var(--st-weight-semibold);
  color: var(--st-shell-text-subtle);
  white-space: nowrap;
}

.title-input {
  font-size: var(--st-text-lg);
  font-weight: var(--st-weight-bold);
  padding: var(--st-space-1) var(--st-space-2);
  border-radius: var(--st-radius-sm);
  border: 1px solid var(--st-accent);
  background: var(--st-surface);
  color: var(--st-text);
  width: 100%;
  min-width: 24rem;
}
</style>
